import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { MERCHANT_ADMIN_EXTRA_SERVICES_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { toDinero, toNumberLossy } from "../../../../currency/currencyLib"
import {
    ExtraServicesQuery,
    GetExtraServiceDetailsResponse,
    getProductDetails,
    updateExtraService,
} from "../extraServicesApi"
import { CreateUpdateExtraServiceForm } from "../components/CreateUpdateExtraServiceForm"
import { CreateUpdateExtraServiceFormValues, ExtraServicePricingType } from "../extraServicesTypes"
import { DateTimeFactory, useDateTime } from "../../../../localization/useDateTime"

const selectProduct = (
    data: GetExtraServiceDetailsResponse,
    dateTimeFactory: DateTimeFactory,
): CreateUpdateExtraServiceFormValues | null => {
    const product = data.extraService
    if (!product) return null

    const timeInterval =
        product.availabilityDetails && product.availabilityDetails.timeInterval
            ? {
                  weekDays: product.availabilityDetails.weekDays,
                  timeInterval: {
                      start: dateTimeFactory.fromHourMinute(product.availabilityDetails.timeInterval.start),
                      end: dateTimeFactory.fromHourMinute(product.availabilityDetails.timeInterval.end),
                  },
              }
            : {
                  weekDays: [],
                  timeInterval: undefined,
              }

    const common = {
        productGroupCode: product.productGroupCode,
        name: product.name,
        description: product.description,
        serviceTime: timeInterval,
        serviceType: product.serviceType,
        isActive: product.isActive,
        action: product.action,
        isMeetingTimeRequired: product.isMeetingTimeRequired,
        isMeetingLocationRequired: product.isMeetingLocationRequired ?? false,
    }

    if (product.pricingType === ExtraServicePricingType.PER_SEAT) {
        return {
            ...common,
            pricingType: ExtraServicePricingType.PER_SEAT,
            minimumPrice: product.minimumPrice ? toNumberLossy(toDinero(product.minimumPrice)) : undefined,
            maximumPrice: product.maximumPrice ? toNumberLossy(toDinero(product.maximumPrice)) : undefined,
            pricePerSeat: product.pricePerSeat ? toNumberLossy(toDinero(product.pricePerSeat)) : undefined,
            minimumPriceCurrency: product.minimumPrice?.currency ?? "",
            maximumPriceCurrency: product.maximumPrice?.currency ?? "",
            pricePerSeatCurrency: product.pricePerSeat?.currency ?? "",
            defaultPrice: undefined,
            defaultCurrency: "",
        }
    }

    return {
        ...common,
        pricingType: ExtraServicePricingType.FLAT_FEE,
        defaultPrice: product.price ? toNumberLossy(toDinero(product.price)) : undefined,
        defaultCurrency: product.price?.currency ?? "DKK",
        minimumPrice: undefined,
        maximumPrice: undefined,
        pricePerSeat: undefined,
        minimumPriceCurrency: "",
        maximumPriceCurrency: "",
        pricePerSeatCurrency: "",
    }
}

const Product = forwardRef<FormikProps<CreateUpdateExtraServiceFormValues>>((props, ref) => {
    const { extraServiceId } = useParams()
    const dateTimeFactory = useDateTime()
    const navigate = useNavigate()

    const productResult = useApiQuery(getProductDetails, {
        queryName: ExtraServicesQuery.DETAILS,
        dependencies: {
            productId: Number(extraServiceId) ?? 0,
        },
        enabled: Boolean(extraServiceId),
        select: (data) => selectProduct(data, dateTimeFactory),
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateProduct } = useApiMutation(updateExtraService, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ExtraServicesQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(ExtraServicesQuery.DETAILS, { productId: params.productId }),
            })
            navigate(MERCHANT_ADMIN_EXTRA_SERVICES_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateExtraServiceForm
                ref={ref}
                initialValues={productResult.data}
                onSubmit={(data) => {
                    extraServiceId && callUpdateProduct({ product: data, productId: Number(extraServiceId) })
                }}
            />
        </>
    )
})

export function EditExtraService() {
    const productFormRef = useRef<FormikProps<CreateUpdateExtraServiceFormValues>>(null)

    const handleSaveClick = () => {
        if (productFormRef.current) {
            productFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="extra-services-edit-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_EXTRA_SERVICES}
                    title="Edit extra service"
                    rightElement={
                        <LocalizedStrict id="extra-services-edit-submit-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-services">
                            <BreadcrumbLink to={MERCHANT_ADMIN_EXTRA_SERVICES_ROUTE}>Extra services</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-service-edit">
                            <Breadcrumb>Edit extra service</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Product ref={productFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
